import {Injectable} from '@angular/core';
import {ApolloQueryResult} from '@apollo/client/core';
import {Apollo, gql, MutationResult} from 'apollo-angular';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {PagedRequest} from '../../models/api/shared/paged/paged-request';
import {PagedResponse} from '../../models/api/shared/paged/paged-response.interface';
import {TypeSerializerUtils} from '../../utils/type-serializer.util';
import {Structure, StructureArray} from '../../models/entities/structure';
import {StructureRequest} from '../../models/api/services/structure-requests.interface';
import {Preparer, PreparerArray} from '../../models/entities/preparer';

@Injectable({
  providedIn: 'root'
})
export class StructuresService {
  constructor(
    private readonly apollo: Apollo
  ) {}

  addStructure(data: StructureRequest): Observable<MutationResult<Structure>> {
    const mutation = gql`
      mutation addStructure($data: StructureRequest) {
        actors {
          structures {
            add(data: $data) {
              id
              active
              name
              name2
              address
              city
              postalCode
              country

              hasVat
              isAutoInvoiceEnabled
              registrationId
              vatNumber
              legalStatus
              shareCapital
              rcsNumber
              rcsCity
              nafCode
              otherInvoiceInfo
              logo {
                id
                url
              }
              preparers {
                metadata {
                  totalResults
                }
              }
              customerSites {
                metadata {
                  totalResults
                }
              }
              contacts {
                id
                name
                sendMail
                mail
                phoneNumber
                comment
              }
            }
          }
        }
      }
    `;

    return this.apollo.mutate<{ actors: { structures: { add: Structure } } }>({
      mutation,
      variables: {
        data: {
          name: data.name,
          name2: data.name2,
          address: data.address,
          city: data.city,
          postalCode: data.postalCode,
          country: data.country,
          latitude: data.latitude,
          longitude: data.longitude,
          contacts: data.contacts,
          logo: data.logo,
          hasVat: data.hasVat,
          isAutoInvoiceEnabled: data.isAutoInvoiceEnabled,
          registrationId: data.registrationId,
          vatNumber: data.vatNumber,
          legalStatus: data.legalStatus,
          shareCapital: data.shareCapital,
          rcsNumber: data.rcsNumber,
          rcsCity: data.rcsCity,
          nafCode: data.nafCode,
          otherInvoiceInfo: data.otherInvoiceInfo

        }
      },
      context: {
        useMultipart: true
      }
    }).pipe(map(result => ({

      data: TypeSerializerUtils.deserialize(result.data?.actors?.structures?.add, Structure),
      errors: result.errors,
      extensions: result.extensions,
      loading: result.loading
    })));
  }

  updateStructure(data: StructureRequest): Observable<MutationResult<Structure>> {
    const mutation = gql`
      mutation updateStructure($id: Guid!, $data: StructureRequest) {
        actors {
          structures {
            update(id: $id, data: $data) {
              id
              active
              name
              name2
              address
              city
              postalCode
              country
              hasVat
              isAutoInvoiceEnabled
              registrationId
              vatNumber
              legalStatus
              shareCapital
              rcsNumber
              rcsCity
              nafCode
              otherInvoiceInfo

              logo {
                id
                url
              }
              preparers {
                metadata {
                  totalResults
                }
              }
              customerSites {
                metadata {
                  totalResults
                }
              }
              contacts {
                id
                name
                sendMail
                mail
                phoneNumber
                comment
              }
            }
          }
        }
      }
    `;

    return this.apollo.mutate<{ actors: { structures: { update: Structure } } }>({
      mutation,
      variables: {
        id: data.id,
        data: {
          active: data.active,
          name: data.name,
          name2: data.name2,
          address: data.address,
          city: data.city,
          postalCode: data.postalCode,
          country: data.country,
          latitude: data.latitude,
          longitude: data.longitude,
          contacts: data.contacts,
          logo: data.logo,
          hasVat: data.hasVat,
          isAutoInvoiceEnabled: data.isAutoInvoiceEnabled,
          registrationId: data.registrationId,
          vatNumber: data.vatNumber,
          legalStatus: data.legalStatus,
          shareCapital: data.shareCapital,
          rcsNumber: data.rcsNumber,
          rcsCity: data.rcsCity,
          nafCode: data.nafCode,
          otherInvoiceInfo: data.otherInvoiceInfo

        }
      },
      context: {
        useMultipart: true
      }
    }).pipe(map(result => ({

      data: TypeSerializerUtils.deserialize(result.data?.actors?.structures?.update, Structure),
      errors: result.errors,
      extensions: result.extensions,
      loading: result.loading
    })));
  }

  deleteStructure(...ids: string[]): Observable<MutationResult<void>> {
    const query = `
      mutation deleteStructure(` + ids.map((_, index) => '$id' + index + ': Guid!').join(', ') + `) {
        actors {
          structures {\n` + ids.map((_, index) => 'id' + index + ': delete (id: $id' + index + ')').join('\n') + `\n}
        }
      }
    `;

    const variables = ids.reduce((response, item, index) => {
      response['id' + index] = item;
      return response;
    }, {});

    return this.apollo.mutate<void>({
      mutation: gql(query),
      variables
    });
  }

  deleteOneStructureHard(id: string): Observable<MutationResult<void>> {
    const mutation = gql`
      mutation deleteStructureActor($id: Guid!, $hard: Boolean) {
        actors {
          structures {
            delete (id: $id, hard: $hard)
          }
        }
      }
    `;

    const variables = {
      id,
      hard: true
    };

    return this.apollo.mutate<void>({
      mutation,
      variables
    });
  }

  structure(id: string): Observable<ApolloQueryResult<Structure>> {
    const query = gql`
      query structure($id: Guid!) {
        actors {
          structure(id: $id) {
            id
            active
            name
            name2
            address
            city
            postalCode
            country
            logo {
              id
              url
            }
            preparers {
              metadata {
                totalResults
              }
            }
            customerSites {
              metadata {
                totalResults
              }
            }
            contacts {
              id
              name
              sendMail
              sendMail
              mail
              phoneNumber
              comment
            }
          }
        }
      }
    `;
    return this.apollo.query<{ actors: { structure: Structure } }>({
      query,
      variables: {id}
    }).pipe(map(result => ({
      data: TypeSerializerUtils.deserialize(result.data?.actors?.structure, Structure),
      error: result.error,
      errors: result.errors,
      partial: result.partial,
      loading: result.loading,
      networkStatus: result.networkStatus
    })));
  }

  structureFull(id: string): Observable<ApolloQueryResult<Structure>> {
    const query = gql`
      query structure($id: Guid!) {
        actors {
          structure(id: $id) {
            id
            active
            name
            name2
            address
            city
            postalCode
            country
            hasVat
            isAutoInvoiceEnabled
            registrationId
            vatNumber
            legalStatus
            shareCapital
            rcsNumber
            rcsCity
            nafCode
            otherInvoiceInfo
            logo {
              id
              url
            }
            preparers {
              metadata {
                totalResults
              }
            }
            customerSites {
              metadata {
                totalResults
              }
            }
            contacts {
              id
              name
              sendMail
              sendMail
              mail
              phoneNumber
              comment
            }
          }
        }
      }
    `;
    return this.apollo.query<{ actors: { structure: Structure } }>({
      query,
      variables: {id}
    }).pipe(map(result => ({
      data: TypeSerializerUtils.deserialize(result.data?.actors?.structure, Structure),
      error: result.error,
      errors: result.errors,
      partial: result.partial,
      loading: result.loading,
      networkStatus: result.networkStatus
    })));
  }


  structurePreparers(id: string): Observable<ApolloQueryResult<PagedResponse<Preparer>>> {
    const query = gql`
      query structurePreparers($id: Guid!) {
        actors {
          structure(id: $id) {
            preparers {
              data {
                id
                active
                name
              },
              metadata {
                totalResults
              }
            }
          }
        }
      }
    `;
    return this.apollo.query<{ actors: { structure: { preparers: PagedResponse<Preparer> } } }>({
      query,
      variables: {id}
    }).pipe(map(result => {
      const raw = result.data?.actors?.structure?.preparers;
      return {
        data: {
          data: TypeSerializerUtils.deserializeArr(raw.data, PreparerArray),
          metadata: raw.metadata
        },
        error: result.error,
        errors: result.errors,
        partial: result.partial,
        loading: result.loading,
        networkStatus: result.networkStatus
      };
    }));
  }

  allStructures(parameters: PagedRequest): Observable<ApolloQueryResult<PagedResponse<Structure>>> {
    const query = gql`
      query structures($offset: Int, $limit: Int, $sortProperty: String, $sortType: ESortType, $search: String) {
        actors {
          structures(offset: $offset, limit: $limit, sortProperty: $sortProperty, sortType: $sortType, search: $search) {
            data {
              id
              active
              name
              name2
              address
              city
              postalCode
              country
              preparers {
                metadata {
                  totalResults
                }
              }
              customerSites {
                metadata {
                  totalResults
                }
              }
            }
            metadata {
              currentPage
              currentResult
              totalPages
              totalResults
              hasMore
            }
          }
        }
      }
    `;
    return this.apollo.query<{ actors: { structures: PagedResponse<Structure> } }>({
      query,
      variables: {
        offset: parameters.offset,
        limit: parameters.limit,
        sortProperty: parameters.sortProperty,
        sortType: parameters.sortType,
        search: parameters.search
      }
    }).pipe(map(result => {
      const raw = result.data?.actors?.structures;
      const response: PagedResponse<Structure> = {
        data: TypeSerializerUtils.deserializeArr(raw.data, StructureArray),
        metadata: raw.metadata
      };
      return {
        data: response,
        error: result.error,
        errors: result.errors,
        partial: result.partial,
        loading: result.loading,
        networkStatus: result.networkStatus
      };
    }));
  }
}
